import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ExternalLink } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel } from "@/pages/Home";
import api from "@/lib/api";

export default function ProjectDetail() {
  const { lang, t, ml } = useI18n();
  const { slug } = useParams();
  const L = (p) => `/${lang}${p}`;
  const [project, setProject] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setProject(null)).finally(() => setLoading(false));
    api.get("/projects").then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  useSEO({
    lang,
    title: project ? `${project.name} | HO-SYS` : "HO-SYS",
    description: project ? ml(project.tagline) : "",
    path: `/projects/${slug}`,
    image: project?.cover,
  });

  if (loading) return <div className="pt-40 text-center text-white/50">{t("common.loading")}</div>;
  if (!project) return <div className="pt-40 text-center text-white/50">404</div>;

  const idx = all.findIndex((p) => p.id === project.id);
  const next = all.length ? all[(idx + 1) % all.length] : null;
  const blocks = [
    { key: "objective", value: ml(project.objective) },
    { key: "problem", value: ml(project.problem) },
    { key: "solution", value: ml(project.solution) },
    { key: "results", value: ml(project.results) },
  ];

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8">
        <Link to={L("/projects")} data-testid="project-back"
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> {t("nav.projects")}
        </Link>
        <Reveal>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="rounded-full bg-neon/10 border border-neon/30 px-3 py-1 font-mono text-xs uppercase tracking-wider text-neon">{ml(project.category)}</span>
            <span className="font-mono text-xs text-white/50">{project.year} · {project.client}</span>
          </div>
          <h1 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl">{project.name}</h1>
          <p className="mt-5 text-white/60 text-xl max-w-2xl">{ml(project.tagline)}</p>
          {project.live_url && (
            <a href={project.live_url} target="_blank" rel="noopener noreferrer"
               data-testid={`project-live-url-${project.id}`}
               className="mt-6 inline-flex items-center gap-2 rounded-full bg-neon px-6 py-3 text-sm font-semibold text-black hover:bg-white transition-colors">
              <ExternalLink className="w-4 h-4" /> Visita el web en directe
            </a>
          )}
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-12">
        <Reveal className="rounded-3xl overflow-hidden border border-white/10">
          <img src={project.cover} alt={project.name} className="w-full h-[300px] md:h-[520px] object-cover" />
        </Reveal>
      </section>

      {project.metrics?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="grid grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {project.metrics.map((mtr, i) => (
              <div key={i} className="bg-[#0A0A0A] p-6 md:p-10 text-center">
                <div className="font-display text-3xl md:text-5xl font-extrabold text-neon">{mtr.value}</div>
                <p className="mt-2 text-white/50 text-xs md:text-sm">{ml(mtr.label)}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-8 space-y-16">
        {blocks.map((b, i) => (
          <Reveal key={b.key} delay={0.04}>
            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-4"><SectionLabel>{t(`projectPage.${b.key}`)}</SectionLabel></div>
              <p className="md:col-span-8 text-white/70 text-lg leading-relaxed">{b.value}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 border-t border-white/10 grid md:grid-cols-2 gap-12">
        <Reveal>
          <h3 className="font-display text-2xl font-bold text-white mb-6">{t("projectPage.features")}</h3>
          <div className="space-y-3">
            {(project.features || []).map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-neon shrink-0" />
                <span className="text-white/80">{ml(f)}</span>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <h3 className="font-display text-2xl font-bold text-white mb-6">{t("projectPage.tech")}</h3>
          <div className="flex flex-wrap gap-2.5">
            {(project.tech || []).map((tech) => (
              <span key={tech} className="rounded-full border border-white/10 bg-[#0A0A0A] px-4 py-2 text-sm text-white/75">{tech}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {next && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 border-t border-white/10">
          <Link to={L(`/projects/${next.id}`)} data-testid="project-next"
            className="group flex items-center justify-between rounded-2xl border border-white/10 bg-[#0A0A0A] p-8 hover:border-neon/40 transition-colors">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-white/40">{t("projectPage.next")}</span>
              <h3 className="mt-2 font-display text-3xl font-bold text-white group-hover:text-neon transition-colors">{next.name}</h3>
            </div>
            <ArrowRight className="w-8 h-8 text-white/40 group-hover:text-neon group-hover:translate-x-1 transition-all" />
          </Link>
        </section>
      )}
    </div>
  );
}
